'use client';

import { useVirtualizer } from '@tanstack/react-virtual';
import { memo, type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ProductLineProduct } from '@/app/components/icons/ProductLineProduct';
import { cn } from '@/app/utils';
import type { AnchorItem } from '../hooks/use-chat-anchors';

interface AnchorConProps {
  /** 上层 useChatAnchors 派生的稳定锚点列表，传 messages 会让组件失稳，禁止 */
  anchors: AnchorItem[];
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  /**
   * 锚点点击回调。AnchorCon 只负责"哪个 anchor 被点了"，调用方决定怎么滚：
   * - Chat 场景：用 anchor.messageIndex 喂给 MessageList virtualizer，配合 buildAnchorSelector 做 fineTune
   * - ShareChat 等纯 DOM 场景：直接 buildAnchorSelector(anchor.id, anchor.role) + scrollToSelector
   */
  onJump: (anchor: AnchorItem) => void;
  className?: string;
}

const ANCHOR_ITEM_ESTIMATE_HEIGHT = 32;
const ANCHOR_OVERSCAN = 8;
// 距离视口顶部 80px 内的锚点目标视为 "当前激活"
const ACTIVE_OFFSET_PX = 80;
// 点击跳转后，短时间内忽略滚动事件对 activeId 的回写。
// 需覆盖 MessageList 的 RAF 补齐循环（最多 40 帧 ≈ 670ms）+ measure 余震窗口。
const JUMP_LOCK_DURATION_MS = 1200;

const AnchorConInner = ({ anchors, scrollContainerRef, onJump, className }: AnchorConProps) => {
  // 锚点列表 = 所有 user 消息 + 有产物（fileList 非空）的 assistant 消息（由 useChatAnchors 上层派生）
  const anchorItems = anchors;

  const anchorIndexMap = useMemo(() => {
    const map = new Map<string, AnchorItem>();
    anchorItems.forEach((item) => {
      map.set(item.id, item);
    });
    return map;
  }, [anchorItems]);

  const [activeId, setActiveId] = useState<string | null>(null);
  const listScrollRef = useRef<HTMLDivElement | null>(null);
  const lastActiveIdRef = useRef<string | null>(null);
  const jumpLockUntilRef = useRef(0);
  const anchorItemsRef = useRef(anchorItems);
  anchorItemsRef.current = anchorItems;

  const virtualizer = useVirtualizer({
    count: anchorItems.length,
    getScrollElement: () => listScrollRef.current,
    estimateSize: () => ANCHOR_ITEM_ESTIMATE_HEIGHT,
    overscan: ANCHOR_OVERSCAN,
    getItemKey: (index) => anchorItems[index]?.id ?? index,
  });
  const measureAnchorItem = useCallback(
    (node: HTMLButtonElement | null) => {
      if (!node) return;
      virtualizer.measureElement(node);
    },
    [virtualizer],
  );

  // 监听父滚动容器，找出当前可视区域内最靠下且仍在阈值上方的锚点目标作为激活项
  useEffect(() => {
    const root = scrollContainerRef.current;
    if (!root || anchorItems.length === 0) return;

    const calcActive = () => {
      // 跳转锁期内，以 handleJump 设定的 activeId 为准，跳过自动计算
      if (Date.now() < jumpLockUntilRef.current) {
        return;
      }

      const nodes = root.querySelectorAll<HTMLElement>('[data-anchor-id]');
      if (nodes.length === 0) return;

      const rootTop = root.getBoundingClientRect().top;
      let candidate: HTMLElement | null = null;
      let candidateTop = Number.NEGATIVE_INFINITY;

      nodes.forEach((node) => {
        const top = node.getBoundingClientRect().top - rootTop;
        if (top - ACTIVE_OFFSET_PX <= 0 && top > candidateTop) {
          candidateTop = top;
          candidate = node;
        }
      });

      // 全部锚点都在阈值之下时，回退取最靠上的一条
      if (!candidate) candidate = nodes[0];
      const id = candidate.getAttribute('data-anchor-id');
      if (id && id !== lastActiveIdRef.current) {
        lastActiveIdRef.current = id;
        setActiveId(id);
      }
    };

    calcActive();

    let raf = 0;
    let lastLoggedTop = -1;
    const handleScroll = () => {
      const top = root.scrollTop;
      // 只在 scrollTop 跳变 >50 时打印，避免刷屏
      if (Math.abs(top - lastLoggedTop) > 50) {
        lastLoggedTop = top;
      }
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(calcActive);
    };

    root.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      root.removeEventListener('scroll', handleScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [scrollContainerRef, anchorItems.length]);

  // 锚点列表自身跟随激活项滚动到可视范围；仅依赖 activeId，避免流式消息更新引发重复 scrollToIndex
  useEffect(() => {
    if (!activeId) return;
    const index = anchorItemsRef.current.findIndex((item) => item.id === activeId);
    if (index < 0) return;
    virtualizer.scrollToIndex(index, { align: 'auto', behavior: 'auto' });
  }, [activeId, virtualizer]);

  const handleJump = useCallback(
    (id: string) => {
      const item = anchorIndexMap.get(id);
      if (!item) return;
      // 锁定 activeId，避免目标滚动过渡期间 calcActive 抢写 activeId
      // （Chat 走 virtualizer 二次 measure；ShareChat 走 smooth scroll 动画）
      jumpLockUntilRef.current = Date.now() + JUMP_LOCK_DURATION_MS;
      lastActiveIdRef.current = id;
      setActiveId(id);

      onJump(item);
    },
    [anchorIndexMap, onJump],
  );

  if (anchorItems.length <= 1) return null;

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div
      className={cn(
        'group -translate-y-1/2 pointer-events-none absolute top-1/2 right-3 z-20 flex max-h-[300px] flex-col overflow-hidden rounded-16',
        // 默认窄列只显示右侧短线/星号；hover 整体展开后铺上 frosted bg + 描边，文案和指示符同时呈现
        'w-6 border border-transparent bg-transparent light:bg-none backdrop-blur-[15px] transition-[width,background-color,border-color] duration-200 ease-out',
        'hover:w-[240px] hover:border-system-white08 light:hover:border-[#11111114] hover:bg-system-white04 light:hover:bg-white/70',
        className,
      )}
    >
      <div
        ref={listScrollRef}
        className={cn(
          'scrollbar-hide pointer-events-auto flex-1 overflow-y-auto',
          // 收起态只留右侧 12px 内边距，让指示符贴近右边缘；展开后切换到 16px 全边距与 Figma 对齐
          'py-2 group-hover:p-3',
        )}
      >
        <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
          {virtualItems.map((virtualItem) => {
            const item = anchorItems[virtualItem.index];
            if (!item) return null;
            const isActive = item.id === activeId;
            const isAssistant = item.role === 'assistant';
            return (
              <button
                type="button"
                key={item.id}
                ref={measureAnchorItem}
                data-index={virtualItem.index}
                onClick={() => handleJump(item.id)}
                className={cn(
                  'group/row absolute top-0 left-0 flex w-full cursor-pointer items-center justify-center gap-0 py-1 text-left font-normal light:text-system-text01 text-sm text-system-white transition-opacity duration-150 group-hover:justify-between group-hover:gap-2',
                  isActive ? 'opacity-100' : 'opacity-40 group-hover:hover:opacity-80',
                )}
                style={{ transform: `translateY(${virtualItem.start}px)` }}
              >
                <span className="hidden min-w-0 flex-1 truncate opacity-0 transition-opacity duration-150 group-hover:block group-hover:opacity-100">
                  {item.label}
                </span>
                <span className="flex size-4 shrink-0 items-center justify-center">
                  {isAssistant ? (
                    <ProductLineProduct
                      className={cn(
                        'size-3 light:text-system-text01 text-system-white transition-transform duration-200 ease-out',
                        isActive ? 'scale-125' : 'scale-100 group-hover/row:scale-125',
                      )}
                    />
                  ) : (
                    <span
                      className={cn(
                        'block h-[2px] rounded-full bg-system-white light:bg-system-text01 transition-[width] duration-200 ease-out',
                        isActive ? 'w-3' : 'w-2 group-hover/row:w-3',
                      )}
                    />
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const AnchorCon = memo(AnchorConInner);
